import { useTheme } from '@mui/material/styles';
import { Box, Button, Checkbox, Chip, IconButton, InputAdornment, InputLabel, MenuItem, Modal, OutlinedInput, Select, SelectChangeEvent, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Label } from "semantic-ui-react";
import { MenuProps, ModalStyle, checkIfMandatory, checkObjectInConstInput, checkObjectInRecordInput, convertToStringList, getFunctionDetails, getStyles, saveMapping } from "../../services/integrationService";
import { conversionType, functionMap, mappingObject, parameterInfo, IntegrationFileInfo, IntegrationMapper, fieldInfoMap } from "../../types/types";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import "./EntityTransformer.css";
import MappingHeader from './MappingHeader';
import { useSelector } from 'react-redux';
import { Asterisk } from 'tabler-icons-react';
import { RegisteredFunctionsButtons } from '../registeredFunctions/RegisteredFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { openPopup } from '../../stateManagement/actions/popupActions';

const defaultMappingState: mappingObject = {
    from: [],
    to: "",
    conversions: []
}

const emptyConversions: conversionType = {
    functionName: null,
    unRegisteredFunction: null,
    constInput: {},
    recordInput: {}
}

export function EntityTransformer(props: {
    fileInfo: IntegrationFileInfo,
    transformer: IntegrationMapper,
    unRegFunctionInfoArr: functionMap[],
    onClose: () => void,
    open: boolean,
    isNew: boolean,
    unRegFuncMap: Map<string, string>,
    setUnRegFuncMap: React.Dispatch<React.SetStateAction<Map<string, string>>>
    columnList: fieldInfoMap[]
}) {

    const remoteTransformerInfo = useSelector((state: any) => {
        let registry = state.integrationRegistry;
        return {
            entityName : registry.entityName,
            lob: registry.lob
        };
    });

    const headerList = props.fileInfo.headers;
    const config = props.transformer.config;

    const [headers, setHeaders] = useState<string[]>([]);
    const [registeredFunctionList, setRegisteredFunctionList] = useState<functionMap[]>([]);
    const [configureList, setConfigureList] = useState<mappingObject[]>([]);
    const [mapperName, setMapperName] = useState<string>(props.transformer.id);
    const [isBusy, setIsBusy] = useState<boolean>(false);

    const convertToMappingObjectList = (stringToParse: string|object) => {
        const list: mappingObject[] = [];
        Object.assign(list, stringToParse);
        setConfigureList(list);
    }

    const handleClose = () => {
        props.onClose();
    }

    const fetchFunctionDetails = useCallback(async () => { // move this api up
        const response = await getFunctionDetails(remoteTransformerInfo.lob);
        const functionList: functionMap[] = Object.values(response);
        setRegisteredFunctionList(functionList);
    }, [])

    useEffect(() => {
        fetchFunctionDetails();
        convertToMappingObjectList(config);
        convertToStringList(headerList,setHeaders,props.isNew,defaultMappingState,setConfigureList)
    }, [config, fetchFunctionDetails, headerList, props.isNew])

    useEffect(() => {
    }, [configureList])

    return (
        <Box>
            <Box display="flex" width={"100%"} justifyContent="space-between" padding={"20px 0 0 0"} alignItems="center">
                <Modal open={props.open} onClose={handleClose}>
                    <Box width={"100%"} sx={ModalStyle} overflow="auto">
                        <IconButton style={{position:'absolute',top:"10px", right:"10px"}} onClick={handleClose} children={<FontAwesomeIcon icon={faXmark} />} />
                        <Table className='entityTable'>
                            <TableHead>
                                <TableRow>
                                    <TableCell align='center' colSpan={4}>
                                        <MappingHeader
                                            unRegFuncMap={props.unRegFuncMap}
                                            setUnRegFuncMap={props.setUnRegFuncMap}
                                            mapperName={mapperName}
                                            setMapperName={setMapperName}
                                            isNew={props.isNew}
                                            onSave={() => saveMapping(
                                                isBusy,
                                                setIsBusy,
                                                configureList,
                                                mapperName,
                                                props.columnList,
                                                props.transformer,
                                                props.isNew,
                                                props.onClose,
                                                props.fileInfo,
                                            )}
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell align='center' colSpan={4} sx={{position:"relative"}}>
                                        <RegisteredFunctionsButtons afterChange={fetchFunctionDetails}/>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {configureList.map((row, index) =>
                                    <MapperTableRow
                                        isNew={props.isNew}
                                        defaultMapping={row}
                                        key={`${row.to}-${row.from}-${index}`}
                                        rowId={index}
                                        sourceList={headers}
                                        columnList={props.columnList}
                                        functionList={registeredFunctionList.concat(props.unRegFunctionInfoArr)}
                                        rowSet={configureList}
                                        updateRows={setConfigureList} />
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                </Modal>
            </Box>
        </Box>
    );
}

function MapperTableRow(props:
    {
        isNew: boolean;
        defaultMapping: mappingObject;
        sourceList: string[];
        columnList: fieldInfoMap[];
        functionList: functionMap[];
        rowId: number;
        rowSet: mappingObject[];
        updateRows: React.Dispatch<React.SetStateAction<mappingObject[]>>
    }
) {
    const [inputList, setInputList] = useState<string[]>([]);
    const [rowState, setRowState] = useState<mappingObject>(props.defaultMapping);

    useEffect(() => {
        props.updateRows(prevState => prevState.map((object, index) => index === props.rowId ? rowState : object));
    }, [rowState])

    return (
        <>
            <TableRow key={props.rowId}
                className='table-class'
                sx={{
                    '&:last-child td': { border: 0 },
                    '&:last-child th': { border: 0 },
                    '&:nth-of-type(even)': { backgroundColor: "white" },
                    '&:nth-of-type(odd)': { backgroundColor: "#EEEEEE" }
                }}>
                <TableCell component={"th"}>
                    <SourceFieldBox
                        inputConversions={rowState}
                        setInputConversions={setRowState}
                        rowId={props.rowId}
                        sourceList={props.sourceList}
                        updateInputList={setInputList} />
                </TableCell>
                <TableCell component={"th"}>
                    <TargetFieldBox
                        inputConversions={rowState}
                        setInputConversions={setRowState}
                        targetList={props.columnList} />
                </TableCell>
                <TableCell component={"th"}>
                    <TargetConverterFunction
                        isNew={props.isNew}
                        inputConversions={rowState}
                        setInputConversions={setRowState}
                        functionList={props.functionList}
                        inputList={inputList} />
                </TableCell>
                <TableCell component={"th"} style={{width:'15%', minWidth:'100px'}}>
                    <RowButtons
                        rowIdx={props.rowId}
                        inputConversions={rowState}
                        setInputConversions={setRowState}
                        rowData={props.rowSet}
                        updateRows={props.updateRows} />
                </TableCell>
            </TableRow>
        </>
    );
}

function SourceFieldBox(props: {
    sourceList: string[];
    updateInputList: React.Dispatch<React.SetStateAction<string[]>>;
    rowId: number;
    inputConversions: mappingObject,
    setInputConversions: React.Dispatch<React.SetStateAction<mappingObject>>
}) {

    const [selectList, setSelectList] = useState<string[]>(props.inputConversions.from);

    useEffect(() => {
        props.setInputConversions(prevState => ({
            ...prevState,
            from: selectList
        }
        ));
        props.updateInputList(selectList);
    }, [selectList]);

    const handleChange = (event: SelectChangeEvent<typeof selectList>) => {
        const {
            target: { value },
        } = event;
        setSelectList(
            typeof value === 'string' ? value.split(',') : value,
        );
        props.updateInputList(typeof value === 'string' ? value.split(',') : value);
    };
    
    const theme = useTheme();

    return (
        <Box>
            <InputLabel id="source-multi-chip-label">Source Field Name</InputLabel>
            <Select
                labelId="source-multi-chip-label"
                id="source-multi-chip"
                multiple
                value={selectList}
                onChange={handleChange}
                input=
                {<OutlinedInput
                    style={{ backgroundColor: "white" }}
                    fullWidth
                    id="select-multiple-chip"
                    label="Chip" />
                }
                renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: "center" }}>
                        {selected.map((value, idx) => (
                            <Chip key={`${value}-${idx}`} label={value} />
                        ))}
                    </Box>
                )}
                MenuProps={MenuProps}
            >
                {props.sourceList.map((source) => (
                    <MenuItem
                        key={source}
                        value={source}
                        style={getStyles(source, selectList, theme)}
                    >
                        {source}
                    </MenuItem>
                ))}
            </Select>
        </Box>);
}

function TargetFieldBox(props: {
    targetList: fieldInfoMap[];
    unusedList?: string[];
    inputConversions: mappingObject,
    setInputConversions: React.Dispatch<React.SetStateAction<mappingObject>>
}) {

    const updateTarget = (event: SelectChangeEvent<string>) => {
        props.setInputConversions(prevState => ({
            ...prevState,
            to: event.target.value
        }))
    }

    return (<Box>
        <InputLabel id="target-field-label">Target Field Name</InputLabel>
        <Select
            labelId="target-field-label"
            defaultValue={""}
            onChange={updateTarget}
            input=
            {<OutlinedInput
                style={{ backgroundColor: "white" }}
                fullWidth
                id="target-field-label"
                label="Chip" />
            }
            value={props.inputConversions.to}
            renderValue={(selected) => selected}
        >
            {props.targetList.length !== 0 && props.targetList.map((targetOption, index) => (
                <MenuItem
                    key={targetOption.name}
                    value={targetOption.name}
                    selected={props.targetList.length > 0}
                >
                    {targetOption.name}
                    { checkIfMandatory(targetOption) && <Asterisk size={18} strokeWidth={2} color={'#bf4040'}/>}
                </MenuItem>
            ))}
        </Select>
    </Box>);
}


function TargetConverterFunction(props: {
    isNew: boolean
    functionList: functionMap[];
    inputList: string[];
    inputConversions: mappingObject,
    setInputConversions: React.Dispatch<React.SetStateAction<mappingObject>>
}) {
    const defaultFunction: functionMap = {
        id: "none",
        parametersInfo: []
    }
    const functionId = props.inputConversions.conversions.length > 0 ? (props.inputConversions.conversions[0].functionName != null ? props.inputConversions.conversions[0].functionName : "none") : "none";
    const newFunction = props.functionList.find(s => s.id === functionId) || defaultFunction
    const defaultConversions: conversionType = (props.inputConversions.conversions.length > 0) ? props.inputConversions.conversions[0] : emptyConversions;
    const [activeFunction, setActiveFunction] = useState<functionMap>(newFunction);
    const [openParamModal, setOpenParamModal] = useState<boolean>(false);
    const [conversionList, setConversions] = useState<conversionType>(defaultConversions);
    const [defaultVal, setDefaultval] = useState<boolean>(props.isNew);

    useEffect(()=>{
        setActiveFunction(newFunction)
    },[props.functionList])

    useEffect(() => {
        props.setInputConversions(prevState => (
            {
                ...prevState,
                "conversions": [conversionList]
            }
        ))
    }, [conversionList])

    const handleFunctionChange = (event: SelectChangeEvent) => {
        const selectedFunction: functionMap = props.functionList.find(object => object.id === event.target.value) || defaultFunction;
        setActiveFunction(selectedFunction);
        const updatedConversions = { ...emptyConversions }
        updatedConversions.functionName = event.target.value === "none" ? null : event.target.value;
        updatedConversions.unRegisteredFunction = selectedFunction.functionBody || null;
        setDefaultval(true);
        setConversions(updatedConversions);
    }

    const setFunctionParameters = () => {
        setOpenParamModal(true)
    }
    
    const closeParamModal = () => {
        if(activeFunction.id!=='none'){
            const requiredParams = activeFunction.parametersInfo.filter((parameter) => parameter.required === true).map(object=>object.parameterName);
            let checkCondition = true;
            if(requiredParams.length >0){
                if(requiredParams.filter((param) => !(checkObjectInConstInput(param, props.inputConversions) || checkObjectInRecordInput(param, props.inputConversions))).length > 0){
                    openPopup('Alert','Required parameters has not been filled');
                    checkCondition = false;
                }
            }
            if(checkCondition === true)
                setOpenParamModal(false);
        }else{
            setOpenParamModal(false);
        }
    }
    const ParameterModal = { ...ModalStyle };
    ParameterModal.width = "60%";
    ParameterModal.height = "90%"

    return (<Box position={'relative'}>
        <InputLabel id="target-function-field-label">Target Converter Function</InputLabel>
        <Select
            labelId="target-function-field-label"
            defaultValue={""}
            input=
            {<OutlinedInput
                style={{ backgroundColor: "white" }}
                fullWidth
                id="target-function-label"
                label="Chip" />
            }
            value={activeFunction.id}
            onChange={handleFunctionChange}
        >
            <MenuItem key="none" value="none">{"none"}</MenuItem>
            {props.functionList.map(functionObj => (
                <MenuItem
                    key={functionObj.id}
                    value={functionObj.id}>
                    {functionObj.id}
                </MenuItem>))
            }
        </Select>
        <Box marginTop={"0.5rem"} style={{
            margin:'auto',
            position: 'absolute',
            left:'50%',
            transform: 'translate(-55%, 10%)',
        }}>
            {activeFunction.id !== 'none' && <Button size='small' variant="contained" onClick={setFunctionParameters}>Parameters</Button>}
        </Box>
        <Modal open={openParamModal} onClose={closeParamModal}>
            <Box width={"100%"} sx={ParameterModal} overflow="auto">
            <IconButton style={{position:'absolute',top:"10px", right:"10px",zIndex:100}} onClick={closeParamModal} children={<FontAwesomeIcon icon={faXmark} />} />
                <Table>
                    <TableHead>
                        <TableRow className='parameter-table-header'>
                            <TableCell component={"th"}><Label>Parameter Name</Label></TableCell>
                            <TableCell component={"th"}><Label>Constant</Label></TableCell>
                            <TableCell component={"th"}><Label>Value</Label></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {activeFunction.parametersInfo.map((parameter, index) =>
                            <TargetConverterMappingRow
                                isNew={defaultVal}
                                key={index}
                                rowParameter={parameter}
                                sourceList={props.inputList}
                                rowId={index}
                                inputConversion={conversionList}
                                updateConversions={setConversions}></TargetConverterMappingRow>
                        )}
                    </TableBody>
                </Table>
            </Box>
        </Modal>
    </Box>);
}

function TargetConverterMappingRow(props: {
    isNew: boolean
    rowParameter: parameterInfo;
    sourceList: string[];
    rowId: number,
    inputConversion: conversionType,
    updateConversions: React.Dispatch<React.SetStateAction<conversionType>>
}) {

    const theme = useTheme();
    const checkValue = props.isNew ? props.sourceList.length > props.rowId : false;

    const [selectedRowParam, setSelected] = useState<string[]>([]);
    const [check, setCheck] = useState<boolean>(!checkValue);

    const updateChecked = (event: { target: { checked: boolean | ((prevState: boolean) => boolean); }; }) => {
        setCheck(event.target.checked);
        if (!event.target.checked)
            delete props.inputConversion.constInput[props.rowParameter.parameterName];
        else
            delete props.inputConversion.recordInput[props.rowParameter.parameterName];
        setSelected([]);
    }

    useEffect(() => {
        const recordKeys = Object.keys(props.inputConversion.recordInput);
        const constKeys = Object.keys(props.inputConversion.constInput);
        if (constKeys.length > 0 && constKeys.find(object => object === props.rowParameter.parameterName) !== undefined) {
            setCheck(true);
            setSelected(props.inputConversion.constInput[props.rowParameter.parameterName]);
        } else if (recordKeys.length > 0 && recordKeys.find(object => object === props.rowParameter.parameterName) !== undefined) {
            setCheck(false);
            setSelected(props.inputConversion.recordInput[props.rowParameter.parameterName]);
        }
    }, [props.inputConversion]);

    const handleSetSelected = (event: SelectChangeEvent<string[]> | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const {
            target: { value },
        } = event;
        setSelected(typeof value === 'string' ? value.split(',') : value);
        if (check) {
            props.updateConversions(prevState => (
                {
                    ...prevState, "constInput": {
                        ...prevState.constInput, [props.rowParameter.parameterName]: typeof value === 'string' ? value.split(',') : value
                    }
                }
            ));
        } else {
            props.updateConversions(prevState => (
                {
                    ...prevState, "recordInput": {
                        ...prevState.recordInput, [props.rowParameter.parameterName]: typeof value === 'string' ? value.split(',') : value
                    }
                }
            ));
        }
    }

    return (<TableRow className='parameter-table-class' sx={{
        '&:last-child td': { border: 0 },
        '&:last-child th': { border: 0 },
        '&:nth-of-type(even)': { backgroundColor: "white" },
        '&:nth-of-type(odd)': { backgroundColor: "#EEEEEE" }
    }}>
        <TableCell component={"th"}>
            <TextField
                label = {"Parameter Name"}
                contentEditable={false}
                fullWidth
                required = {props.rowParameter.required}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="start">
                            {props.rowParameter.required && <Asterisk size={18} strokeWidth={2} color={'#bf4040'}/>}
                        </InputAdornment>
                    )
                }}
                value={props.rowParameter.parameterName}>
            </TextField>
        </TableCell>
        <TableCell component={"th"} width={'15%'}>
            <Checkbox
                checked={check}
                onChange={updateChecked}
            ></Checkbox>
        </TableCell>
        <TableCell component={"th"}>
            {!check && (<Select
                id="function-multi-select"
                multiple
                value={selectedRowParam}
                onChange={handleSetSelected}
                input=
                {<OutlinedInput
                    style={{ backgroundColor: "white"}}
                    fullWidth
                    id="select-multiple-chip"
                    label="Chip" />
                }
                renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: "center" }}>
                        {selected.map((value) => (
                            <Chip key={value} label={value} />
                        ))}
                    </Box>
                )}
                MenuProps={MenuProps}
            >
                {props.sourceList.map((source) => (
                    <MenuItem
                        key={source}
                        value={source}
                        style={getStyles(source, selectedRowParam, theme)}
                    >
                        {source}
                    </MenuItem>
                ))}
            </Select>)}
            {check && (<TextField 
                required = {props.rowParameter.required}
                margin='none' 
                value={selectedRowParam} 
                onChange={handleSetSelected} 
                label={"Enter a value"}>
            </TextField>)}
        </TableCell>
    </TableRow>);
}


function RowButtons(props: {
    rowIdx: number,
    inputConversions: mappingObject,
    setInputConversions: React.Dispatch<React.SetStateAction<mappingObject>>,
    rowData: mappingObject[]
    updateRows: React.Dispatch<React.SetStateAction<mappingObject[]>>
}) {

    const handleAdd = () => {
        props.updateRows((newList: mappingObject[]) => newList.flatMap((a, idx) => idx === props.rowIdx ? [a, defaultMappingState] : a));
    }

    const handleRemove = () => {
        props.updateRows((newList: mappingObject[]) => newList.filter((a, idx) => idx !== props.rowIdx));
    }

    return (<Box gap={"0.5em"} display={"flex"} justifyContent={"center"}>
        <IconButton onClick={handleAdd}><AddCircleIcon></AddCircleIcon></IconButton>
        <IconButton onClick={handleRemove}><DeleteIcon></DeleteIcon></IconButton>
    </Box>);
}